import * as React from 'react';
import { useState, useEffect } from 'react';

import { SnowSearchTopView, SnowSearchTopMobileView, JpSnowSearchTopView, JpSnowSearchTopMobileView } from './views/SnowSearchContainerView';
import FieldKeyword from './FieldKeyword';

import ItemSubmitButton from './ItemSubmitButton';
import ItemSummariesView from './views/ItemSummariesView';
import ItemSummary from './ItemSummary';
import {
  SNOW_SEARCH_FIELD_FOCUSED, SNOW_SEARCH_FIELD_BLURRED,
  SNOW_SEARCH_ITEM_SUGGESTION_SELECTED
} from './events';
import { IFieldType, IItemFilter, IItemSuggestionList, IItemSuggestion, SnowItemCandidateList } from './types';
import {
  wait, searchSnowItemFromResort, searchSnowItemFromArea, searchSnowItemFromPrefecture,
  defaultItemCount, defaultItemSuggestionList
} from './helpers';
import { getLocale } from '../utils/pathHelper';
import t from './locales';

interface IProps {
  q: IItemSuggestion | null;
  snow_item_candidate_list: SnowItemCandidateList;
  recommended_snow_list?: IItemSuggestionList;
  isMobile: boolean;
  is_toppage: boolean;
}

const SnowSearch: React.FC<IProps> = (props) => {
  const { q, isMobile, is_toppage } = props;
  const [focusedField, setFocusedField] = useState<IFieldType>('');
  const [keyword, setKeyword] = useState<string>(q && q.label || '');
  const [suggestionLabelHidden, hideSuggestionLabel] = useState(false);
  const [suggestionList, setSuggestionList] = useState<IItemSuggestionList>(props.recommended_snow_list || defaultItemSuggestionList);
  const [selectedSuggestion, setSuggestion] = useState<IItemSuggestion>((q && q.type && q.label) ? q : null);
  const [currentCount, setCount] = useState<{ [key in IItemFilter]: number }>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const snowItemCandidateList = props.snow_item_candidate_list;
  const locale = getLocale({ underscore: true });
  const totalCount = defaultItemCount();
  const [isTargetBlank, setIsTargetBlank] = useState<boolean>(true);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);

  function handleKeywordChange(event: React.ChangeEvent) {
    const value = (event.target as HTMLInputElement).value;
    const _searchByKeyword = (keyword: string) => {
      return {
        resort: searchSnowItemFromResort(value, locale, snowItemCandidateList.resort, snowItemCandidateList.prefecture, snowItemCandidateList.area),
        prefecture: searchSnowItemFromPrefecture(value, locale, snowItemCandidateList.prefecture),
        area: searchSnowItemFromArea(value, locale, snowItemCandidateList.area),
      };
    }
    const results = _searchByKeyword(value);
    setSuggestionList(results);
    if (results.resort.length == 0) {
      const labelNotFound: IItemSuggestion = {type: null, id: null, label: t.notFoundResorts(), lift_count: null, lesson_count: null, rental_count: null, total_count: null}
      setSuggestionList({resort: [labelNotFound]});
    }
    setKeyword(value);
    if (value.length > 0) {
      setIsTargetBlank(false);
      setDisableSubmit(true);
    } else {
      setIsTargetBlank(true);
      setDisableSubmit(false);
    }
  }

  // 背景クリックでバックドロップを非表示にする
  const bodyElement: HTMLElement = document.getElementsByTagName('body')[0];

  useEffect(() => {
    const handleFocus = (event: CustomEvent) => {
      setFocusedField(event.detail.focusedField);
    }
    const handleBlur = () => {
      setFocusedField('');
    }
    const handleSuggestionSelected = async (event: CustomEvent) => {
      const selected: IItemSuggestion = event.detail.suggestion;
      if (selected.id != null) {
        setDisableSubmit(false);
        setSuggestion(selected);
        hideSuggestionLabel(false);
        setFocusedField('');
        setLoading(true);
        setCount({ lift: selected.lift_count, lesson: selected.lesson_count, rental: selected.rental_count });
        await wait();
        setLoading(false);
      }
    }
    const handleBlurAreaClick = (event: MouseEvent) => {
      const suggestionSelected: Boolean = isDropDownElem(event);

      // Suggestion以外へのクリックはメニューを閉じる
      if (!suggestionSelected) {
        document.dispatchEvent(new CustomEvent(SNOW_SEARCH_FIELD_BLURRED));
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      // 27 = escキー
      if (event.keyCode === 27) {
        document.dispatchEvent(new CustomEvent(SNOW_SEARCH_FIELD_BLURRED));
      }
    }
    const isDropDownElem = (elem) => {
      let target = elem.target;
      // Suggestionのエレメントの中に最大7階層のタグが入っているので、7個上の親Nodeを探してみる
      // 途中bodyタグになったらもう抜ける
      for (let i = 1; i < 8; i++) {
        if (target.className.match(/^DropDownView/)) {
          return true;
        }
        if (target.nodeName == 'BODY') {
          return false;
        }
        target = target.parentNode;
      }
      return false;
    }
    document.addEventListener(SNOW_SEARCH_FIELD_FOCUSED, handleFocus, { capture: true });
    document.addEventListener(SNOW_SEARCH_FIELD_BLURRED, handleBlur, { capture: true });
    document.addEventListener(SNOW_SEARCH_ITEM_SUGGESTION_SELECTED, handleSuggestionSelected, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    bodyElement.addEventListener('click', handleBlurAreaClick, { capture: true });
    return () => {
      document.removeEventListener(SNOW_SEARCH_FIELD_FOCUSED, handleFocus, { capture: true });
      document.removeEventListener(SNOW_SEARCH_FIELD_BLURRED, handleBlur, { capture: true });
      document.removeEventListener(SNOW_SEARCH_ITEM_SUGGESTION_SELECTED, handleSuggestionSelected, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
      bodyElement.removeEventListener('click', handleBlurAreaClick, { capture: true });
    }
  }, []);

  useEffect(() => {
    if (focusedField === 'keyword') {
      setSuggestion(null);
      hideSuggestionLabel(true);
    }
  }, [focusedField]);

  if (locale == 'ja') {
    const Container = isMobile ? JpSnowSearchTopMobileView : JpSnowSearchTopView;
    return (
      <Container>
        <FieldKeyword
          searchContext="item"
          placement="top"
          placeholder="スキー場名、県名、エリアを入力"
          isDropDownShown={focusedField === 'keyword'}
          selectedSuggestion={selectedSuggestion}
          suggestionList={suggestionList}
          labelHidden={suggestionLabelHidden}
          handleChange={handleKeywordChange} />
        <div>
          <ItemSubmitButton
            label={(isTargetBlank && selectedSuggestion == null) ? "全てのスキー場" : "リフト券を探す"}
            keyword={keyword}
            selectedSuggestion={selectedSuggestion}
            disabled={disableSubmit}
          />
        </div>
      </Container>
    );
  } else {
    const Container = isMobile ? SnowSearchTopMobileView : SnowSearchTopView;
    if (is_toppage) {
      return (
        <Container>
          <div className='wrapper'>
            <div className='search-inner'>
              <FieldKeyword
                searchContext="item"
                placement="top"
                placeholder={t.allResorts()}
                isDropDownShown={focusedField === 'keyword'}
                selectedSuggestion={selectedSuggestion}
                suggestionList={suggestionList}
                labelHidden={suggestionLabelHidden}
                handleChange={handleKeywordChange} />
              <ItemSummariesView>
                <ItemSummary
                  isLoading={isLoading}
                  icon="icon-lift_solid"
                  filter="lift"
                  count={selectedSuggestion ? currentCount.lift : totalCount.lift}
                  suggestion={selectedSuggestion}
                />
                <ItemSummary
                  isLoading={isLoading}
                  icon="icon-lesson_solid"
                  filter="lesson"
                  count={selectedSuggestion ? currentCount.lesson : totalCount.lesson}
                  suggestion={selectedSuggestion}
                />
                <ItemSummary
                  isLoading={isLoading}
                  icon="icon-rental_solid"
                  filter="rental"
                  count={selectedSuggestion ? currentCount.rental : totalCount.rental}
                  suggestion={selectedSuggestion}
                />
              </ItemSummariesView>
            </div>
          </div>
          <div style={{ width: '800px', display: 'flex', justifyContent: 'flex-end', marginTop: '1em' }}>
            <ItemSubmitButton
              label={(isTargetBlank && selectedSuggestion == null) ? t.allResorts() : (selectedSuggestion && selectedSuggestion.type === 'resort' ? t.seeItemsForThisResort() : t.compareResorts())}
              keyword={keyword}
              selectedSuggestion={selectedSuggestion}
              disabled={disableSubmit}
            />
          </div>
        </Container>
      );
    } else {
      return(
        <Container>
          <div className='wrapper'>
            <div className='search-inner'>
              <FieldKeyword
                searchContext="item"
                placement="top"
                placeholder={t.allResorts()}
                isDropDownShown={focusedField === 'keyword'}
                selectedSuggestion={selectedSuggestion}
                suggestionList={suggestionList}
                labelHidden={suggestionLabelHidden}
                handleChange={handleKeywordChange} />
              <ItemSummariesView>
                <ItemSummary
                  isLoading={isLoading}
                  icon="icon-lift_solid"
                  filter="lift"
                  count={selectedSuggestion ? currentCount.lift : totalCount.lift}
                  suggestion={selectedSuggestion}
                />
                <ItemSummary
                  isLoading={isLoading}
                  icon="icon-lesson_solid"
                  filter="lesson"
                  count={selectedSuggestion ? currentCount.lesson : totalCount.lesson}
                  suggestion={selectedSuggestion}
                />
                <ItemSummary
                  isLoading={isLoading}
                  icon="icon-rental_solid"
                  filter="rental"
                  count={selectedSuggestion ? currentCount.rental : totalCount.rental}
                  suggestion={selectedSuggestion}
                />
              </ItemSummariesView>
            </div>
          </div>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: '1em' }}>
            <ItemSubmitButton
              label={(isTargetBlank && selectedSuggestion == null) ? t.allResorts() : (selectedSuggestion && selectedSuggestion.type === 'resort' ? t.seeItemsForThisResort() : t.compareResorts())}
              keyword={keyword}
              selectedSuggestion={selectedSuggestion}
              disabled={disableSubmit}
            />
          </div>
        </Container>
      );
    }
  }
};

export default SnowSearch;
